import React, { CSSProperties, FC, useMemo } from "react"
import { YoutubePlayerText } from "../components/YoutubePlayerText";
import { ExternalLinkText } from "../components/ExternalLinkText"
import { PageLinkText } from "./PageLinkText"
import { UserType } from "../types/models/User";
import { API_HOST } from "../constant/const"

let postRegExp: RegExp
if ( API_HOST.includes( "staging" ) ) {
    postRegExp = /(https:\/\/(?:www.)?staging\-club\-cloud\.work\/post\/\d+\/?)/im
}
else if ( API_HOST.includes( "api.club-cloud.jp" ) ) {
    postRegExp = /(https:\/\/(?:www.)?club\-cloud\.jp\/post\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/?)/im
}
else {
    postRegExp = /(http:\/\/127.0.0.1:8002\/post\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/?)/im
}

let postIDExtractReg: RegExp
if ( API_HOST.includes( "staging" ) ) {
    postIDExtractReg = /https:\/\/(?:www.)?staging\-club\-cloud\.work\/post\/(\d+)\/?/im
}
else if ( API_HOST.includes( "api.club-cloud.jp" ) ) {
    postIDExtractReg = /https:\/\/(?:www.)?club\-cloud\.jp\/post\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/?/im
}
else {
    postIDExtractReg = /http:\/\/127.0.0.1:8002\/post\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/?/im
}

const youtubeRegExp = /(https?:\/\/(?:.+.)?youtube\.com\/watch\?v=[-_.!~*\'()a-zA-Z0-9;\/?:\@&=+\$,%#]+)/im
const shortYoutubeRegExp = /(https?:\/\/(?:www.)?youtu\.be\/[-_.!~*\'()a-zA-Z0-9;\/?:\@&=+\$,%#]+)/im
const generalWebSiteURLReg = /\n?(https?:\/\/(?:.+.)?(?:.+)\.[A-Z]+(?:\/[-_.!~*\'()a-zA-Z0-9;\/?:\@&=+\$,%#]+)?)\n?/im

type regObjectType = {
    type: RegType
    reg: RegExp
}

const Reg = {
    POST_LINK: "postLink",
    YOUTUBE: "youtube",
    YOUTUBE_SHOORT: "youtube_short",
    GENERAL: "generalWebSite",
    MENTION: "teamMemberMention"
} as const

type RegType = typeof Reg[ keyof typeof Reg ]

const regObjectList: regObjectType[] = [
    { type: Reg.POST_LINK, reg: postRegExp },
    { type: Reg.YOUTUBE, reg: youtubeRegExp },
    { type: Reg.YOUTUBE_SHOORT, reg: shortYoutubeRegExp },
    { type: Reg.GENERAL, reg: generalWebSiteURLReg }
]

// let post = "https://club-cloud.jp/post/100/"
// let youtube = "https://www.youtube.com/watch?v=RONcote5KTc"
// let short = "https://youtu.be/RONcote5KTc"
// var testText = post + " " + youtube + "ま" + youtube + ' ' + short + ' ' + "まま" + short + "ここ"
// console.log( testText )

const baseTextStyle: CSSProperties = {
    display: 'block', textDecoration: "underline",
    padding: "0px 4px", fontWeight: 'bold', cursor: "pointer"
}

type Props = {
    children: string,
    teamMemberList: UserType[],
    textStyle?: CSSProperties
}

const LinkIncludedText: FC<Props> = ( {
    children,
    teamMemberList,
    textStyle = {}
} ) =>
    useMemo( () => {

        textStyle = { ...baseTextStyle, ...textStyle }

        const addTeamMemberReg = ( regObjectList: regObjectType[] ) => {
            const teamMemberRegList = teamMemberList.map( member => ( {
                type: Reg.MENTION,
                reg: generateTeamMemberReg( member )
            } ) )
            return regObjectList.concat( teamMemberRegList )
        }

        const generateTeamMemberReg = ( member: UserType ) => {
            const mention = `(@${ member.name })`
            const memberReg = new RegExp( mention, "im" );
            return memberReg
        }

        const textArray = splitByReg( children, addTeamMemberReg( regObjectList ), textStyle )

        return (
            <div style={ { flexDirection: "row", flexWrap: "wrap", alignItems: "center", lineHeight: 1.5 } }>
                { textArray }
            </div> )

    }, [ children, teamMemberList ] )

type returnType = JSX.Element | undefined | returnType[]

const splitByReg = ( text: string, regObjectList: regObjectType[], textStyle: CSSProperties ): returnType => {

    if ( !regObjectList.length ) return <PlainText key={ text }>{ text }</PlainText>

    const regObject = regObjectList[ 0 ]
    const type = regObject.type
    const reg = regObject.reg
    const splitedTextArray = text.split( reg )

    return splitedTextArray.map( text => reg.test( text ) ?
        generateLink( text, type, textStyle ) :
        splitByReg( text, regObjectList.slice( 1 ), textStyle )
    );
}


//リンクちょうどの文字列に対して、リンクに変えて渡す
const generateLink = ( text: string, type: RegType, textStyle: CSSProperties ) => {

    if ( type === Reg.POST_LINK ) {
        const match = text.match( postIDExtractReg )
        if ( !match ) return
        const postID = match[ 1 ]
        const postURLMatch = text.match( postRegExp )
        return (
            <PageLinkText postID={ postID } key={ text }>
                <p style={ { ...textStyle, cursor: "inherit", textDecoration: "none", fontSize: 12, fontWeight: "normal", marginTop: 8 } }>
                    { postURLMatch && postURLMatch[ 0 ] }
                </p>
            </PageLinkText>
        )
    }
    if ( type === Reg.YOUTUBE ) {
        return <YoutubePlayerText videoURL={ text } textStyle={ textStyle } key={ text } />
    }
    if ( type === Reg.YOUTUBE_SHOORT ) {
        return <YoutubePlayerText videoURL={ text } textStyle={ textStyle } key={ text } />
    }
    if ( type === Reg.GENERAL ) {
        return <ExternalLinkText href={ text } textStyle={ textStyle } key={ text } />
    }
    if ( type === Reg.MENTION ) {
        return <p style={ { fontWeight: "bold", ...textStyle, cursor: "inherit", textDecoration: "none" } } key={ text }>{ text }</p>
    }
}

const PlainText = ( {
    children
}: { children: string } ) =>
    children ?
        <span style={ { whiteSpace: "pre-wrap" } } key={ JSON.stringify( children ) }>
            { children }
        </span>
        : null

export default LinkIncludedText


